/**
 * wappen-slider.js
 *
 * Native Wappen Slider – Custom Element
 *
 * Architektur:
 *  - Extends HTMLElement (keine externe Library)
 *  - Nutzt die Scroller-Klasse aus @theme/scrolling für smooth scroll +
 *    scroll-end-Erkennung – keine Kopie, keine Neuimplementierung
 *  - CSS scroll-snap übernimmt das eigentliche Snap-Verhalten nativ
 *  - Pagination-Dots werden dynamisch per JS erzeugt
 *  - Alle konfigurierbaren Werte kommen aus data-Attributen
 *    (geschrieben vom Liquid-Section-Template)
 *
 * Sichtbarkeit von Arrows & Dots:
 *  - Desktop / Tablet (> 749px): sichtbar nur wenn mehr als 3 Cards vorhanden
 *  - Mobile (<= 749px):          sichtbar ab 2 Cards
 *  - Gesteuert per CSS-Klasse .slider-nav--hidden (kein inline-style)
 *  - Reagiert auf Breakpoint-Wechsel (Gerätedrehung, Resize) via matchMedia
 *
 * @see assets/scrolling.js       – Scroller-Klasse
 * @see assets/testimonial-slider.js – Referenz-Implementierung
 * @see sections/wappen-cards.liquid  – Liquid-Template mit data-Attributen
 */

import { Scroller } from '@theme/scrolling';

class WappenSliderComponent extends HTMLElement {
  // ─── Private State ────────────────────────────────────────────────

  /** @type {Scroller|null} */
  #scroller = null;

  /** @type {number} Aktuell aktiver Slide-Index */
  #currentIndex = 0;

  /** @type {number|undefined} Autoplay Interval-ID */
  #interval = undefined;

  /** @type {HTMLElement[]} Alle Slide-Elemente (.wappen-card-block) */
  #slides = [];

  /** @type {HTMLButtonElement[]} Dot-Buttons (dynamisch erzeugt) */
  #dots = [];

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  /** @type {MediaQueryList|null} Breakpoint-Listener für Mobile-Erkennung */
  #mql = null;

  // ─── Lifecycle ────────────────────────────────────────────────────

  connectedCallback() {
    // Alle Card-Blöcke als Slides verwenden
    this.#slides = Array.from(this.querySelectorAll('.wappen-card-block'));

    // Slider ist deaktiviert oder nur 1 Card – statisch rendern
    if (this.dataset.enableSlider !== 'true' || this.#slides.length <= 1) {
      return;
    }

    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  // ─── Initialisierung ──────────────────────────────────────────────

  /** Vollständige Slider-Initialisierung */
  #setup() {
    const scrollerEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-scroller]'));
    if (!scrollerEl) return;

    // ── Scroller (aus @theme/scrolling) ────────────────────────────
    // Übernimmt Scroll-Events, Throttling und das scroll-end Promise.
    this.#scroller = new Scroller(scrollerEl, {
      onScroll: this.#handleScroll,
      onScrollEnd: this.#onScrollEnd,
    });

    // ── Touch (native horizontal scroll ist bereits aktiv via overflow-x) ──
    if (this.dataset.touch !== 'false') {
      scrollerEl.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    }

    // ── Maus-Drag ──────────────────────────────────────────────────
    if (this.dataset.mouseDrag === 'true') {
      this.#setupMouseDrag(scrollerEl);
    }

    // ── Navigation aufbauen ────────────────────────────────────────
    // Arrows und Dots sind immer vorhanden wenn via Liquid aktiviert;
    // JS steuert nur die Sichtbarkeit über .slider-nav--hidden.
    if (this.dataset.showArrows === 'true') {
      this.#setupArrows();
    }

    if (this.dataset.showDots === 'true') {
      this.#setupDots();
    }

    // ── Autoplay ───────────────────────────────────────────────────
    if (this.dataset.autoplay === 'true') {
      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('mouseenter', this.#pauseAutoplay);
        this.addEventListener('mouseleave', this.#resumeAutoplay);
      }
      this.#startAutoplay();
    }

    // ── ResizeObserver – Re-Snap nach Layout-Neuberechnung ─────────
    this.#resizeObserver = new ResizeObserver(() => {
      const slide = this.#slides[this.#currentIndex];
      if (slide) this.#scroller?.to(slide, { instant: true });
      this.#updateNavVisibility();
      this.#updateOverflowState();
    });
    this.#resizeObserver.observe(scrollerEl);

    // ── Breakpoint-Listener – reagiert auf Gerätedrehung ──────────
    this.#mql = window.matchMedia('(max-width: 749px)');
    this.#mql.addEventListener('change', this.#updateNavVisibility);

    // ── Initialzustand setzen ──────────────────────────────────────
    this.#updateDots(0);
    this.#updateArrows(0);
    this.#updateNavVisibility();
    this.#updateOverflowState();
  }

  // ─── Overflow-State ──────────────────────────────────────────────

  /**
   * Setzt die Klasse .wappen-slider--overflowing wenn der Scroll-Track
   * breiter ist als der sichtbare Bereich. Nur dann wird justify-content:
   * flex-start per CSS erzwungen; andernfalls greift die Ausrichtungs-
   * Klasse des Grids (z. B. --center) weiterhin.
   */
  #updateOverflowState = () => {
    const scrollerEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-scroller]'));
    if (!scrollerEl) return;
    const isOverflowing = scrollerEl.scrollWidth > scrollerEl.clientWidth;
    this.classList.toggle('wappen-slider--overflowing', isOverflowing);
  };

  // ─── Nav-Sichtbarkeitslogik ───────────────────────────────────────

  /**
   * Berechnet ob Arrows und Dots sichtbar sein sollen.
   *
   * Navigation ist sichtbar genau dann wenn der Scroll-Track tatsächlich
   * überläuft (scrollWidth > clientWidth), d. h. nicht alle Cards gleichzeitig
   * sichtbar sind – unabhängig von Card-Anzahl oder Breakpoint.
   *
   * Steuert Sichtbarkeit über CSS-Klasse .slider-nav--hidden auf dem
   * Controls-Container – kein inline-style.
   */
  #updateNavVisibility = () => {
    const scrollerEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-scroller]'));
    // Navigation anzeigen wenn der Scroll-Track tatsächlich überläuft,
    // d. h. nicht alle Cards gleichzeitig sichtbar sind.
    const shouldShow = scrollerEl ? scrollerEl.scrollWidth > scrollerEl.clientWidth : false;

    const controls = /** @type {HTMLElement|null} */ (this.querySelector('.wappen-slider__controls'));
    if (controls) {
      controls.classList.toggle('slider-nav--hidden', !shouldShow);
    }
  };

  // ─── Scroll-Handling ──────────────────────────────────────────────

  /**
   * Wird vom Scroller während des Scrollens aufgerufen (gedrosselt).
   * Synchronisiert den aktiven Index mit dem am weitesten links sichtbaren Slide.
   */
  #handleScroll = () => {
    const scrollerEl = this.querySelector('[data-scroller]');
    if (!scrollerEl || !this.#slides.length) return;

    const index = this.#nearestSlideIndex(scrollerEl);

    if (index !== this.#currentIndex) {
      this.#currentIndex = index;
      this.#updateDots(index);
      this.#updateArrows(index);
    }
  };

  /**
   * Wird vom Scroller nach dem Scrollen aufgerufen.
   * Snapped exakt auf den nächsten Slide um etwaigen Drift zu korrigieren.
   */
  #onScrollEnd = () => {
    const scrollerEl = this.querySelector('[data-scroller]');
    if (!scrollerEl || !this.#slides.length) return;

    const index = this.#nearestSlideIndex(scrollerEl);
    this.#currentIndex = index;
    this.#updateDots(index);
    this.#updateArrows(index);
  };

  /**
   * Gibt den Index des Slides zurück, der dem aktuellen scrollLeft am nächsten ist.
   * @param {HTMLElement} scrollerEl
   * @returns {number}
   */
  #nearestSlideIndex(scrollerEl) {
    const { scrollLeft } = scrollerEl;
    let nearestIndex = 0;
    let minDist = Infinity;

    this.#slides.forEach((slide, i) => {
      const dist = Math.abs(slide.offsetLeft - scrollLeft);
      if (dist < minDist) {
        minDist = dist;
        nearestIndex = i;
      }
    });

    return nearestIndex;
  }

  // ─── Öffentliche Navigation API ───────────────────────────────────

  /**
   * Scrollt zu einem bestimmten Slide per Index.
   * Nutzt Scroller.to() – identisches Muster wie in slideshow.js.
   * @param {number} index
   */
  select(index) {
    if (!this.#slides.length || !this.#scroller) return;

    const infinite = this.dataset.infinite === 'true';
    let target = index;

    /*
     * Wrapping erkennen bevor target geklemmt wird.
     * Beim Wrap-Around (z.B. letzte → erste Card) würde ein smooth-scroll
     * über mehrere Snap-Points laufen. iOS Safari / Mobile Chrome stoppen
     * dabei am ersten gefundenen Snap-Point (auch bei programmatischem
     * scrollTo). Fix: Wrap-Around immer als instant ausführen.
     */
    const isWrapping = infinite && (index < 0 || index >= this.#slides.length);

    if (infinite) {
      if (target < 0) target = this.#slides.length - 1;
      if (target >= this.#slides.length) target = 0;
    } else {
      target = Math.max(0, Math.min(target, this.#slides.length - 1));
    }

    const slide = this.#slides[target];
    if (!slide) return;

    // Instant beim Wrap-Around, smooth bei normaler Navigation
    this.#scroller.to(slide, { instant: isWrapping });

    this.#currentIndex = target;
    this.#updateDots(target);
    this.#updateArrows(target);
  }

  /** Zum nächsten Slide */
  next() {
    this.select(this.#currentIndex + 1);
  }

  /** Zum vorherigen Slide */
  previous() {
    this.select(this.#currentIndex - 1);
  }

  // ─── Dot-Navigation ───────────────────────────────────────────────

  /** Erzeugt Dot-Buttons dynamisch im [data-dots-container] */
  #setupDots() {
    const container = /** @type {HTMLElement|null} */ (this.querySelector('[data-dots-container]'));
    if (!container) return;

    // Container leeren (z. B. nach Theme-Editor-Reload)
    container.innerHTML = '';
    this.#dots = [];

    for (let i = 0; i < this.#slides.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'wappen-slider__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      // Farbe kommt ausschließlich per CSS (weiß)
      dot.addEventListener('click', () => this.select(i));
      container.appendChild(dot);
      this.#dots.push(dot);
    }
  }

  /**
   * Aktualisiert aria-selected auf allen Dots um den aktiven Slide zu markieren.
   * CSS selektiert [aria-selected="true"] für die aktive Farbe.
   * @param {number} index
   */
  #updateDots(index) {
    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === index));
    });
  }

  // ─── Arrow-Navigation ─────────────────────────────────────────────

  /** Verbindet Arrow-Buttons mit den Navigationsmethoden */
  #setupArrows() {
    const prev = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-prev]'));
    const next = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-next]'));

    prev?.addEventListener('click', () => this.previous());
    next?.addEventListener('click', () => this.next());
  }

  /**
   * Deaktiviert Arrow-Buttons an den Grenzen wenn kein Infinite-Loop aktiv ist.
   * @param {number} index
   */
  #updateArrows(index) {
    const infinite = this.dataset.infinite === 'true';
    const prev = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-prev]'));
    const next = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-next]'));

    if (prev) prev.disabled = !infinite && index === 0;
    if (next) next.disabled = !infinite && index >= this.#slides.length - 1;
  }

  // ─── Autoplay ─────────────────────────────────────────────────────

  /** Startet den Autoplay-Intervall */
  #startAutoplay() {
    if (this.#interval !== undefined) return;

    const speed = parseInt(this.dataset.autoplaySpeed ?? '3000', 10);

    this.#interval = /** @type {number} */ (
      setInterval(() => {
        // Kein Vorschub wenn Tab/Seite im Hintergrund ist
        if (document.hidden) return;
        this.next();
      }, speed)
    );
  }

  #pauseAutoplay = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  #resumeAutoplay = () => {
    if (this.dataset.autoplay === 'true') {
      this.#startAutoplay();
    }
  };

  // ─── Maus-Drag ────────────────────────────────────────────────────

  /**
   * Aktiviert Click-and-Drag-Scrollen auf dem Track-Container.
   * @param {HTMLElement} scrollerEl
   */
  #setupMouseDrag(scrollerEl) {
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    scrollerEl.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX;
      scrollStart = scrollerEl.scrollLeft;
      // CSS cursor:grabbing via :active – kein inline-style nötig
      e.preventDefault(); // Verhindert Textmarkierung beim Ziehen
    });

    const stopDrag = () => {
      isDown = false;
    };

    scrollerEl.addEventListener('mouseup', stopDrag);
    scrollerEl.addEventListener('mouseleave', stopDrag);

    scrollerEl.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      scrollerEl.scrollLeft = scrollStart - dx;
    });
  }

  // ─── Touch (vertikales Scrollen schützen) ─────────────────────────

  /** @type {number} */
  #touchStartX = 0;
  /** @type {number} */
  #touchStartY = 0;

  #handleTouchStart = (/** @type {TouchEvent} */ e) => {
    const touch = e.touches[0];
    if (!touch) return;
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  // ─── Cleanup ──────────────────────────────────────────────────────

  /** Räumt alle Listener und Timer auf – verhindert Memory Leaks */
  #cleanup() {
    // Autoplay-Timer stoppen
    clearInterval(this.#interval);
    this.#interval = undefined;

    // Scroller zerstören (entfernt den scroll-EventListener)
    this.#scroller?.destroy();
    this.#scroller = null;

    // ResizeObserver trennen
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    // Breakpoint-Listener entfernen
    this.#mql?.removeEventListener('change', this.#updateNavVisibility);
    this.#mql = null;

    // Hover-Listener entfernen wenn Autoplay aktiv war
    if (this.dataset.pauseOnHover === 'true') {
      this.removeEventListener('mouseenter', this.#pauseAutoplay);
      this.removeEventListener('mouseleave', this.#resumeAutoplay);
    }
  }
}

// Custom Element einmalig registrieren
if (!customElements.get('wappen-slider-component')) {
  customElements.define('wappen-slider-component', WappenSliderComponent);
}
